import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Download, ArrowRight, Building2, Users, Target, Brain } from 'lucide-react';
import { brandAssets } from '../data/mock';

const CORPORATE_PROFILE_URL = "https://customer-assets.emergentagent.com/job_9dea0385-ce0b-4daa-adfc-ca84c0eb189a/artifacts/e56czl5s_RESILIGENT%20Corporate%20Profile%20%272026.pdf";

// Social share image - branded image for social media previews
const SOCIAL_SHARE_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9dea0385-ce0b-4daa-adfc-ca84c0eb189a/images/371d16a231c568bdef1c1a690d5b36bcad62080f18731b110296afd294a13e6c.png";

const CorporateProfilePage = () => {
  // Auto-download on page load for direct links
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('auto') === 'true') {
      window.open(CORPORATE_PROFILE_URL, '_blank');
    }
  }, []);

  const handleDownload = () => {
    window.open(CORPORATE_PROFILE_URL, '_blank');
  };

  const capabilities = [
    { icon: Target, title: "Diagnostics & Insights", desc: "Evidence-based leadership assessment" },
    { icon: Users, title: "Executive Coaching", desc: "ICF MCC certified coaches" },
    { icon: Brain, title: "Capability Building", desc: "Bespoke leadership programmes" },
    { icon: Building2, title: "Advisory & Change", desc: "Organisational transformation" }
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Helmet>
        <title>Corporate Profile | RESILIGENT - Leadership Development Africa</title>
        <meta name="description" content="Download RESILIGENT's corporate profile. Leadership development, executive coaching, diagnostics, and organisational transformation across Africa." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://resiligent.com/corporate-profile" />
        <meta property="og:title" content="RESILIGENT Corporate Profile 2026" />
        <meta property="og:description" content="Leadership Development. African Context. Download our corporate profile to learn how we partner with leading organisations across Africa." />
        <meta property="og:image" content={SOCIAL_SHARE_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="RESILIGENT" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://resiligent.com/corporate-profile" />
        <meta name="twitter:title" content="RESILIGENT Corporate Profile 2026" />
        <meta name="twitter:description" content="Leadership Development. African Context. Download our corporate profile to learn how we partner with leading organisations across Africa." />
        <meta name="twitter:image" content={SOCIAL_SHARE_IMAGE} />
        
        {/* LinkedIn */}
        <meta property="og:locale" content="en_GB" />
        <meta name="author" content="RESILIGENT" />
      </Helmet>
      {/* Header */}
      <header className="bg-[#0a1628] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <a href="/" className="flex items-center">
            <img 
              src={brandAssets.logoWhite} 
              alt="RESILIGENT" 
              className="h-10 w-auto"
            />
          </a>
          <a 
            href="/"
            className="text-white/70 hover:text-white text-sm transition-colors"
          >
            Visit Website
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6">
          
          {/* Hero Section */}
          <div className="text-center mb-16">
            <p className="text-[#00A6DA] text-sm font-medium uppercase tracking-wider mb-4">
              Corporate Profile 2026
            </p>
            <h1 className="text-white font-bold text-3xl sm:text-4xl lg:text-5xl mb-6 leading-tight">
              Leadership Development.<br />
              <span className="text-[#00A6DA]">African Context.</span>
            </h1>
            <p className="text-slate-400 text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto">
              A comprehensive overview of our leadership diagnostics, executive coaching, capability building, and organisational transformation capabilities.
            </p>
          </div>

          {/* Download Card */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 sm:p-12 mb-16">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-[#00A6DA]/20 rounded-2xl flex items-center justify-center">
                  <Download size={48} className="text-[#00A6DA]" />
                </div>
              </div>
              <div className="flex-1 text-center lg:text-left">
                <h2 className="text-white font-semibold text-xl sm:text-2xl mb-3">
                  Download Our Corporate Profile
                </h2>
                <p className="text-slate-400 mb-6">
                  Learn how RESILIGENT partners with leading organisations across Africa to build leadership capability, strengthen culture, and drive performance.
                </p>
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-3 bg-[#00A6DA] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#0090c0] focus:outline-none focus:ring-2 focus:ring-[#00A6DA] focus:ring-offset-2 focus:ring-offset-[#0a1628] transition-all duration-300"
                >
                  Download PDF
                  <Download size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Capabilities Grid */}
          <div className="mb-16">
            <h3 className="text-white/60 text-sm font-medium uppercase tracking-wider text-center mb-8">
              What's Inside
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {capabilities.map((cap, index) => (
                <div 
                  key={index}
                  className="bg-white/5 border border-white/10 rounded-xl p-5 text-center hover:border-[#00A6DA]/30 transition-colors"
                >
                  <cap.icon size={28} className="text-[#00A6DA] mx-auto mb-3" />
                  <h4 className="text-white font-medium text-sm mb-1">{cap.title}</h4>
                  <p className="text-slate-500 text-xs">{cap.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center border-t border-white/10 pt-12">
            <p className="text-slate-400 mb-4">Want to discuss how we can support your organisation?</p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 text-[#00A6DA] font-medium hover:underline"
            >
              Book a confidential conversation
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0a1628] border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <img 
              src={brandAssets.logoWhite} 
              alt="RESILIGENT" 
              className="h-8 w-auto opacity-60"
            />
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} RESILIGENT. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CorporateProfilePage;
